import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  Typography,
  Paper,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import paymentIcon from "../../../static/media/icon/payment.svg";
import xmlIcon from "../../../static/media/icon/xml.svg";

const tdLabelSx = {
  width: 180,
  bgcolor: "#f3f3f3",
  borderRight: "1px solid #e0e0e0",
  color: "#333",
  fontWeight: 600,
  verticalAlign: "top",
};

const tdFieldSx = { p: 1, verticalAlign: "middle" };
const inputSx = { width: "50%" };

const normalizeTipoDocumento = (doc) => {
  const raw = String(doc?.tipo_documento_label || doc?.tipo_documento || "")
    .trim()
    .toUpperCase()
    .replaceAll("-", "_")
    .replaceAll(" ", "_");

  if (raw === "01" || raw === "FACTURA") return "FACTURA";
  if (raw === "04" || raw === "NOTA_CREDITO" || raw === "NOTA_DE_CREDITO") return "NOTA_CREDITO";
  if (raw === "05" || raw === "NOTA_DEBITO" || raw === "NOTA_DE_DEBITO") return "NOTA_DEBITO";
  if (raw === "06" || raw === "GUIA_REMISION" || raw === "GUIA_DE_REMISION") return "GUIA_REMISION";
  if (raw === "07" || raw === "RETENCION" || raw === "COMPROBANTE_RETENCION") return "RETENCION";

  return raw;
};

const getTipoDocumentoTexto = (doc, esCompra) => {
  if (doc?.tipo_documento_texto) return doc.tipo_documento_texto;
  if (esCompra) return "Compra";

  const tipo = normalizeTipoDocumento(doc);

  if (tipo === "NOTA_CREDITO") return "Nota de Crédito";
  if (tipo === "NOTA_DEBITO") return "Nota de Débito";
  if (tipo === "GUIA_REMISION") return "Guía de Remisión";
  if (tipo === "RETENCION") return "Retención";
  if (tipo === "FACTURA") return "Factura";

  return tipo || "Documento";
};

const puedeRegistrarPago = (doc, esCompra) => {
  if (doc?.permisos && typeof doc.permisos.permite_pago === "boolean") {
    return doc.permisos.permite_pago;
  }

  if (esCompra) return true;

  const tipo = normalizeTipoDocumento(doc);

  if (tipo === "GUIA_REMISION") return false;
  if (tipo === "RETENCION") return false;
  if (tipo === "NOTA_CREDITO") return false;

  return true;
};

const puedeDescargarXml = (doc, esCompra) => {
  if (doc?.permisos && typeof doc.permisos.permite_xml === "boolean") {
    return doc.permisos.permite_xml;
  }

  return !esCompra;
};

const cleanRows = (rows) => {
  return (rows || []).filter((row) => {
    const value = row?.value;
    return value !== null && value !== undefined && String(value).trim() !== "";
  });
};

const InfoInvoice = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchDoc = async () => {
      setLoading(true);
      setError(false);

      try {
        const response = await fetch(`/facturas/documentos/${id}/`, {
          credentials: "include",
        });

        const contentType = (response.headers.get("content-type") || "").toLowerCase();

        if (!contentType.includes("application/json")) {
          throw new Error();
        }

        if (!response.ok) {
          throw new Error();
        }

        const data = await response.json();
        setDoc(data);
      } catch {
        setError(true);
        setDoc(null);
      } finally {
        setLoading(false);
      }
    };

    fetchDoc();
  }, [id]);

  const esCompra = String(doc?.origen || "").toUpperCase() === "COMPRA";
  const origen = esCompra ? "COMPRA" : "VENTA";
  const personaDetalle = doc?.persona_detalle || {};
  const identificacion = personaDetalle.identificacion || "";
  const personaId =
    personaDetalle.persona_id ||
    personaDetalle.id ||
    doc?.persona_id ||
    doc?.cliente_id ||
    doc?.proveedor_id ||
    "";

  const tipoDocumentoTexto = getTipoDocumentoTexto(doc, esCompra);
  const mostrarPago = puedeRegistrarPago(doc, esCompra);
  const mostrarXml = puedeDescargarXml(doc, esCompra);
  const mostrarVendedor = doc?.vendedor && doc.vendedor.nombre;

  const extraRows = useMemo(() => {
    if (!doc) return [];

    if (Array.isArray(doc.extra_info) && doc.extra_info.length) {
      return cleanRows(doc.extra_info);
    }

    const tipo = normalizeTipoDocumento(doc);

    if (tipo === "GUIA_REMISION") {
      const g = doc.guia_remision || {};
      return cleanRows([
        { label: "Transportista", value: g.transportista_razon_social },
        { label: "Identificación transportista", value: g.transportista_identificacion },
        { label: "Placa", value: g.placa },
        { label: "Dirección de partida", value: g.direccion_partida },
        { label: "Dirección de destino", value: g.direccion_destino },
        { label: "Motivo de traslado", value: g.motivo_traslado },
        { label: "Ruta", value: g.ruta },
        { label: "Inicio transporte", value: g.fecha_inicio_transporte },
        { label: "Fin transporte", value: g.fecha_fin_transporte },
        { label: "Documento sustento", value: g.numero_documento_sustento },
        { label: "Fecha documento sustento", value: g.fecha_documento_sustento },
      ]);
    }

    if (tipo === "NOTA_CREDITO") {
      const rel = (doc.documentos_relacionados || [])[0] || {};
      return cleanRows([
        { label: "Tipo de nota de crédito", value: doc.tipo_nota_credito_texto || doc.tipo_nota_credito },
        { label: "Documento modificado", value: rel.numero_documento || doc.numero_documento_modificado },
        { label: "Fecha documento modificado", value: rel.fecha_emision || doc.fecha_documento_modificado },
        { label: "Autorización documento modificado", value: rel.autorizacion },
        { label: "Motivo / descripción", value: doc.descripcion || doc.descripcion_factura },
      ]);
    }

    if (tipo === "NOTA_DEBITO") {
      const rel = (doc.documentos_relacionados || [])[0] || {};
      return cleanRows([
        { label: "Tipo de nota de débito", value: doc.tipo_nota_debito_texto || doc.tipo_nota_debito },
        { label: "Motivo nota de débito", value: doc.motivo_nota_debito },
        { label: "Documento modificado", value: rel.numero_documento || doc.numero_documento_modificado },
        { label: "Fecha documento modificado", value: rel.fecha_emision || doc.fecha_documento_modificado },
        { label: "Autorización documento modificado", value: rel.autorizacion },
      ]);
    }

    if (tipo === "RETENCION") {
      return cleanRows(
        (doc.retenciones_detalle || []).flatMap((r, idx) => [
          { label: `Retención ${idx + 1} tipo`, value: r.tipo },
          { label: `Retención ${idx + 1} código`, value: r.codigo_retencion || r.codigo },
          { label: `Retención ${idx + 1} base`, value: r.base_imponible },
          { label: `Retención ${idx + 1} porcentaje`, value: r.porcentaje },
          { label: `Retención ${idx + 1} valor`, value: r.valor },
        ])
      );
    }

    return cleanRows([
      { label: "Vencimiento (días)", value: doc.vencimiento_dias || 0 },
      { label: "Entregado", value: doc.entregado ? "Sí" : "No" },
    ]);
  }, [doc]);

  const goToPayment = () => {
    navigate(`/admin-panel/pagos/${id}?origen=${origen}&persona_id=${personaId || ""}`, {
      state: {
        origen,
        persona_id: personaId || "",
        cliente_id: esCompra ? null : personaId || "",
        proveedor_id: esCompra ? personaId || "" : null,
        documento_id: id,
        persona: {
          id: personaId || "",
          persona_id: personaId || "",
          cliente_id: esCompra ? null : personaId || "",
          proveedor_id: esCompra ? personaId || "" : null,
          identificacion,
          tipo_persona: personaDetalle.tipo_persona || (esCompra ? "PROVEEDOR" : "CLIENTE"),
          razon_social: personaDetalle.razon_social || doc?.persona || "",
        },
      },
    });
  };

  if (loading) {
    return (
      <Box sx={{ mt: 5, textAlign: "center" }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Cargando documento…</Typography>
      </Box>
    );
  }

  if (error || !doc) {
    return (
      <Box sx={{ mt: 5, textAlign: "center" }}>
        <Typography color="error">Documento no encontrado</Typography>
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 3, borderRadius: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Información del Documento – {esCompra ? "Compra" : "Venta"}
        </Typography>

        <Box sx={{ display: "flex", gap: 1 }}>
          {mostrarXml && (
            <Tooltip title="Descargar XML" arrow>
              <IconButton onClick={() => window.open(`/facturas/documentos/${id}/xml/`, "_blank")}>
                <img src={xmlIcon} alt="Descargar XML" style={{ width: 26, height: 26 }} />
              </IconButton>
            </Tooltip>
          )}

          {mostrarPago && (
            <Tooltip title="Registrar Pago" arrow>
              <IconButton onClick={goToPayment}>
                <img src={paymentIcon} alt="Registrar Pago" style={{ width: 28, height: 28 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      <Table>
        <TableBody>
          <TableRow>
            <TableCell sx={tdLabelSx}>Fecha de Emisión:</TableCell>
            <TableCell sx={tdFieldSx}>
              <TextField value={doc.fecha_emision || ""} InputProps={{ readOnly: true }} sx={inputSx} />
            </TableCell>

            <TableCell sx={tdLabelSx}>Tipo de Documento:</TableCell>
            <TableCell sx={tdFieldSx}>
              <TextField value={tipoDocumentoTexto} InputProps={{ readOnly: true }} sx={inputSx} />
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell sx={tdLabelSx}># Documento:</TableCell>
            <TableCell sx={tdFieldSx}>
              <TextField value={doc.numero_documento || ""} InputProps={{ readOnly: true }} sx={inputSx} />
            </TableCell>

            <TableCell sx={tdLabelSx}>Autorización:</TableCell>
            <TableCell sx={tdFieldSx}>
              <TextField value={doc.autorizacion || ""} InputProps={{ readOnly: true }} sx={{ width: "100%" }} />
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell sx={tdLabelSx}>Persona:</TableCell>
            <TableCell sx={tdFieldSx} colSpan={3}>
              <TextField value={doc.persona || ""} InputProps={{ readOnly: true }} sx={{ width: "70%" }} />
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell sx={tdLabelSx}>Identificación:</TableCell>
            <TableCell sx={tdFieldSx} colSpan={3}>
              <TextField value={identificacion} InputProps={{ readOnly: true }} sx={{ width: "70%" }} />
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell sx={tdLabelSx}>Referencia:</TableCell>
            <TableCell sx={tdFieldSx} colSpan={3}>
              <TextField value={doc.referencia || ""} InputProps={{ readOnly: true }} sx={{ width: "70%" }} />
            </TableCell>
          </TableRow>

          {mostrarVendedor && (
            <TableRow>
              <TableCell sx={tdLabelSx}>Vendedor:</TableCell>
              <TableCell sx={tdFieldSx} colSpan={3}>
                <TextField value={doc.vendedor.nombre || ""} InputProps={{ readOnly: true }} sx={{ width: "70%" }} />
              </TableCell>
            </TableRow>
          )}

          {extraRows.map((row, index) => (
            <TableRow key={`${row.label}-${index}`}>
              <TableCell sx={tdLabelSx}>{row.label}:</TableCell>
              <TableCell sx={tdFieldSx} colSpan={3}>
                <TextField value={row.value || ""} InputProps={{ readOnly: true }} sx={{ width: "70%" }} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
};

export default InfoInvoice;